import {
  ConfigurableFocusTrap,
  ConfigurableFocusTrapFactory,
} from '@angular/cdk/a11y';
import {
  ConnectedPosition,
  FlexibleConnectedPositionStrategyOrigin,
  Overlay,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal, ComponentType } from '@angular/cdk/portal';
import {
  Component,
  ComponentRef,
  ElementRef,
  Injector,
  OnDestroy,
  inject,
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { ResizeableComponent } from '../resizeable/resizeable.component';
import { ResizeService } from 'src/app/@shared/services/resize/resize.service';
import { DATA_INJECTION_TOKEN } from 'src/app/@shared/services/overlay/overlay.service';
import { LogService } from 'src/app/@shared/services/log/log.service';
import { v4 as uuidv4 } from 'uuid';
import { Direction } from '@angular/cdk/bidi';

interface ManagedOverlay {
  overlayRef: OverlayRef;
  cardCloseSub?: Subscription;
  escSub?: Subscription;
  closeCallbackFn?: (value: any) => void;
  focusTrap?: ConfigurableFocusTrap;
  /** Element to return focus to after the overlay closes (typically the opener control). */
  restoreFocusTo?: HTMLElement | null;
  /**
   * Layout regression fix: keeps the pane size locked to the host container
   * (e.g. the drawer's inner form column). Without this, CDK overlays opened
   * with width/height '100%' resolve against the full-viewport
   * `.cdk-overlay-connected-position-bounding-box` and spill across the entire
   * screen, leaving the drawer-anchored overlay visually un-clipped on
   * laptop/desktop. Detached when the overlay is torn down.
   */
  containerResizeObserver?: ResizeObserver;
}

@Component({
    template: '',
    standalone: false
})
export class OverlayComponent extends ResizeableComponent implements OnDestroy {
  card: any;

  private readonly configurableFocusTrapFactory = inject(
    ConfigurableFocusTrapFactory
  );

  /** @deprecated Use overlayEntries; kept for rare direct OverlayRef access during migration */
  protected overlayReferences: { [overlayId: string]: OverlayRef } = {};

  protected overlayEntries: { [overlayId: string]: ManagedOverlay } = {};

  /** Legacy single subscription — do not use for new code; nested overlays use per-entry subs */
  protected compSub: Subscription;
  protected compRef: ComponentRef<any>;

  constructor(
    protected readonly overlay: Overlay,
    protected override readonly resizeService: ResizeService
  ) {
    super(resizeService);
  }

  /**
   * Resolve a `FlexibleConnectedPositionStrategyOrigin` to a real DOM element
   * when one is present. We accept `ElementRef`, raw `Element`, and reject
   * `Point` (a bare `{x,y}` literal — used by context-menu / anchor overlays
   * which intentionally don't need container-locked sizing).
   *
   * Layout regression fix: used by `applyContainerSize` and
   * `observeContainerResizes` so a drawer-anchored overlay can be clipped to
   * the drawer's bounding rect rather than the viewport.
   */
  private resolveContainerElement(
    container: FlexibleConnectedPositionStrategyOrigin | undefined
  ): Element | null {
    if (!container) {
      return null;
    }
    if (container instanceof ElementRef) {
      return (container.nativeElement as Element) ?? null;
    }
    if (typeof Element !== 'undefined' && container instanceof Element) {
      return container;
    }
    return null;
  }

  /**
   * Push the current bounding rect of `container` into the overlay's pane size.
   *
   * Layout regression fix: `openComponentOverlay` creates overlays with
   * `width/height: '100%'`. Because of the global
   * `.component-overlay + .cdk-overlay-connected-position-bounding-box`
   * rule that pins the bounding box to `100% 100%` of the
   * `.cdk-overlay-container` (full viewport), the pane otherwise spans the
   * entire viewport and the slide-up-card's `w-full` paints across the whole
   * screen, even when anchored to the right-side drawer. Snapping the pane
   * to the container rect makes the visual width match the drawer width
   * (25–35% laptop/desktop), restoring the pre-regression layout the audit
   * was asking us to preserve.
   *
   * Safe no-op when the container is a Point (no DOM rect) or briefly 0×0
   * (drawer mid-animation, hidden tab).
   */
  private applyContainerSize(
    ref: OverlayRef,
    container: FlexibleConnectedPositionStrategyOrigin | undefined
  ): void {
    const el = this.resolveContainerElement(container);
    if (!el) {
      return;
    }
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      return;
    }
    ref.updateSize({
      width: `${rect.width}px`,
      height: `${rect.height}px`,
    });
    /*
      Layout regression fix follow-up: `OverlayRef.updateSize` only writes the
      new width/height onto the pane's inline style; CDK does NOT re-run the
      position strategy on a size change. The strategy positioned the pane at
      its previous width (100% of bounding-box ≈ full viewport), so the
      pane's left/top inline styles already anchor it to the LEFT edge of
      the viewport. After we shrink the pane to the container's width, the
      pane keeps that left anchor and ends up clipped to the wrong side of
      the screen — exactly the regression where an English address-picker
      flew to the left instead of sitting over the right-anchored drawer.
      `updatePosition()` triggers `FlexibleConnectedPositionStrategy.apply()`
      which re-evaluates origin/overlay alignment for the new pane size and
      moves the right edge back onto the container's right edge.
    */
    ref.updatePosition();
  }

  /**
   * Watch the container element so the pane stays clipped to it across
   * window resizes, orientation changes, and devtools open/close. The drawer
   * width is a percentage of the viewport — without an observer, an overlay
   * sized at attach time gets stuck at the original px width and drifts away
   * from the drawer's actual rect after the first resize.
   *
   * Returns the observer (or undefined if not applicable) so the managed
   * entry can disconnect it during teardown.
   */
  private observeContainerResizes(
    ref: OverlayRef,
    container: FlexibleConnectedPositionStrategyOrigin | undefined
  ): ResizeObserver | undefined {
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const el = this.resolveContainerElement(container);
    if (!el) {
      return undefined;
    }
    const observer = new ResizeObserver(() => {
      this.applyContainerSize(ref, container);
    });
    observer.observe(el);
    return observer;
  }

  /**
   * Tear down focus trap and move focus back to the element that had focus before open.
   */
  private releaseOverlayFocus(entry: ManagedOverlay): void {
    if (entry.focusTrap) {
      entry.focusTrap.destroy();
      entry.focusTrap = undefined;
    }
    const prior = entry.restoreFocusTo;
    entry.restoreFocusTo = undefined;
    if (
      prior &&
      typeof prior.focus === 'function' &&
      this.isElementStillInDocument(prior)
    ) {
      try {
        prior.focus({ preventScroll: true });
      } catch {
        /* ignore focus errors from detached custom elements */
      }
    }
  }

  private isElementStillInDocument(el: HTMLElement): boolean {
    return typeof document !== 'undefined' && document.contains(el);
  }

  /**
   * Detach overlay immediately (no close animation delay). Use when replacing one overlay with another.
   */
  protected detachOverlayImmediate(
    refId: string,
    closeValue?: any,
    runCallback: boolean = true
  ): void {
    const entry = this.overlayEntries[refId];
    if (!entry) {
      return;
    }
    this.releaseOverlayFocus(entry);
    entry.cardCloseSub?.unsubscribe();
    entry.escSub?.unsubscribe();
    entry.containerResizeObserver?.disconnect();
    entry.overlayRef.detach();
    delete this.overlayEntries[refId];
    delete this.overlayReferences[refId];
    if (runCallback && entry.closeCallbackFn) {
      entry.closeCallbackFn(closeValue);
    }
  }

  private scheduleOverlayFinalize(
    refId: string,
    closeValue: any,
    delayMs: number
  ): void {
    const entry = this.overlayEntries[refId];
    if (!entry) {
      return;
    }
    setTimeout(() => {
      const stillThere = this.overlayEntries[refId];
      if (!stillThere) {
        return;
      }
      LogService.logDebug(`Closing overlay ${refId}`, stillThere.overlayRef);
      this.releaseOverlayFocus(stillThere);
      stillThere.cardCloseSub?.unsubscribe();
      stillThere.escSub?.unsubscribe();
      stillThere.containerResizeObserver?.disconnect();
      stillThere.overlayRef.detach();
      delete this.overlayEntries[refId];
      delete this.overlayReferences[refId];
      if (stillThere.closeCallbackFn) {
        stillThere.closeCallbackFn(closeValue);
      }
    }, delayMs);
  }

  /**
   * @param closeOnEscape When false, Escape does not close this overlay (e.g. main map pane).
   * @param trapFocus When false, no CDK focus trap (persistent map pane must not hijack Tab from the drawer).
   */
  openComponentOverlay<T>(
    direction: Direction,
    componentType: ComponentType<T>,
    data?: any,
    container?: FlexibleConnectedPositionStrategyOrigin,
    closeCallbackFn?: (value: any) => void,
    positions?: ConnectedPosition[],
    closeOnEscape: boolean = true,
    trapFocus: boolean = true
  ): string {
    const refId = uuidv4();
    const ref = this.overlay.create({
      /*
        Layout regression fix: CDK's root `Directionality` is read ONCE at
        bootstrap from <html dir>. Since index.html declares <html dir="rtl">
        as the initial default (Hebrew first), CDK's `Overlay.Directionality`
        is permanently 'rtl' even after `AppComponent` flips <html dir> to
        'ltr' on English. Without passing `direction` to `overlay.create`,
        every `FlexibleConnectedPositionStrategy` resolved `start`/`end`
        against that stuck RTL bidi — so a caller in LTR ("end" / "end")
        was being interpreted as "left edge", floating the overlay over the
        OPPOSITE side of the screen from the right-pinned drawer. Forwarding
        the caller's `direction` here makes the strategy's `_isRtl` match
        the per-call intent, restoring the drawer-anchored placement in
        every language.
      */
      direction,
      height: '100%',
      width: '100%',
      hasBackdrop: true,
      backdropClass: ['component-overlay', 'hidden'],
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(container ?? this.card)
        .withFlexibleDimensions(true)
        .withViewportMargin(0)
        .withPush(false)
        .withPositions(
          positions ?? [
            {
              originX: direction === 'rtl' ? 'start' : 'end',
              originY: 'top',
              overlayX: direction === 'rtl' ? 'start' : 'end',
              overlayY: 'top',
            },
          ]
        ),
      scrollStrategy: this.overlay.scrollStrategies.block(),
    });

    const injector = Injector.create({
      providers: [
        {
          provide: DATA_INJECTION_TOKEN,
          useValue: data,
        },
      ],
    });

    const attachedRef = ref.attach(
      new ComponentPortal(componentType, null, injector)
    );
    this.compRef = attachedRef;

    /*
      Layout regression fix: clip the overlay pane to the host container's
      rect right after attach, then keep it in sync on resize. See the
      docstrings on `applyContainerSize` / `observeContainerResizes` for
      details on why width:'100%' alone leaks to the viewport here.
    */
    const containerOrigin = container ?? this.card;
    this.applyContainerSize(ref, containerOrigin);
    const containerResizeObserver = this.observeContainerResizes(
      ref,
      containerOrigin
    );

    const restoreFocusTo = trapFocus
      ? (document.activeElement as HTMLElement | null)
      : null;
    const focusTrap = trapFocus
      ? this.configurableFocusTrapFactory.create(ref.overlayElement)
      : undefined;

    const entry: ManagedOverlay = {
      overlayRef: ref,
      closeCallbackFn,
      focusTrap,
      restoreFocusTo: trapFocus ? restoreFocusTo : undefined,
      containerResizeObserver,
    };

    const instance = attachedRef.instance as {
      onCardClose?: Subject<any>;
      handleOverlayEscape?: () => void;
    };

    if (instance?.onCardClose) {
      entry.cardCloseSub = instance.onCardClose.subscribe((value: any) => {
        this.scheduleOverlayFinalize(refId, value, 150);
      });
    }

    if (closeOnEscape) {
      entry.escSub = ref.keydownEvents().subscribe((event: KeyboardEvent) => {
        if (event.key !== 'Escape') {
          return;
        }
        event.preventDefault();
        event.stopPropagation();

        if (typeof instance?.handleOverlayEscape === 'function') {
          instance.handleOverlayEscape();
        } else {
          this.scheduleOverlayFinalize(refId, null, 0);
        }
      });
    }

    this.overlayEntries[refId] = entry;
    this.overlayReferences[refId] = ref;

    // Defer so projected / *ngIf content is present before first tabbable is resolved.
    if (trapFocus) {
      setTimeout(() => {
        const current = this.overlayEntries[refId];
        if (current?.focusTrap) {
          void current.focusTrap.focusInitialElementWhenReady({
            preventScroll: true,
          });
        }
      }, 0);
    }

    return refId;
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();

    Object.keys(this.overlayEntries)
      .reverse()
      .forEach((refId) => this.detachOverlayImmediate(refId, undefined, false));
  }
}
